import {
  CalendarOutlined,
  DollarOutlined,
  HomeOutlined,
  ToolOutlined,
  TeamOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import type { ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { colors } from "~/styles/tokens";
import { ExploreReveal } from "./ExploreReveal";

const STATEMENTS: {
  id: string;
  headlineKey:
    | "Explore_Statement_Occupancy"
    | "Explore_Statement_Rent"
    | "Explore_Statement_Renewals"
    | "Explore_Statement_Maintenance"
    | "Explore_Statement_Access"
    | "Explore_Statement_Tenants";
  detailKey:
    | "Explore_Statement_Occupancy_Detail"
    | "Explore_Statement_Rent_Detail"
    | "Explore_Statement_Renewals_Detail"
    | "Explore_Statement_Maintenance_Detail"
    | "Explore_Statement_Access_Detail"
    | "Explore_Statement_Tenants_Detail";
  icon: ReactNode;
  tone: "property" | "success" | "warning" | "unit" | "tertiary" | "primary";
}[] = [
  {
    id: "occupancy",
    headlineKey: "Explore_Statement_Occupancy",
    detailKey: "Explore_Statement_Occupancy_Detail",
    icon: <HomeOutlined />,
    tone: "property",
  },
  {
    id: "rent",
    headlineKey: "Explore_Statement_Rent",
    detailKey: "Explore_Statement_Rent_Detail",
    icon: <DollarOutlined />,
    tone: "success",
  },
  {
    id: "renewals",
    headlineKey: "Explore_Statement_Renewals",
    detailKey: "Explore_Statement_Renewals_Detail",
    icon: <CalendarOutlined />,
    tone: "warning",
  },
  {
    id: "maintenance",
    headlineKey: "Explore_Statement_Maintenance",
    detailKey: "Explore_Statement_Maintenance_Detail",
    icon: <ToolOutlined />,
    tone: "primary",
  },
  {
    id: "access",
    headlineKey: "Explore_Statement_Access",
    detailKey: "Explore_Statement_Access_Detail",
    icon: <TeamOutlined />,
    tone: "tertiary",
  },
  {
    id: "tenants",
    headlineKey: "Explore_Statement_Tenants",
    detailKey: "Explore_Statement_Tenants_Detail",
    icon: <UserAddOutlined />,
    tone: "unit",
  },
];

const TONE_COLOR: Record<(typeof STATEMENTS)[number]["tone"], string> = {
  property: colors.actionPrimary,
  success: colors.statusSuccess,
  warning: colors.statusWarning,
  unit: colors.actionSecondary,
  tertiary: colors.actionTertiary,
  primary: colors.actionPrimary,
};

export function ExploreValueSection() {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <section className="explore-section explore-section--surface-white">
      <div className="explore-section-inner">
        <ExploreReveal>
          <p className="explore-kicker">{t("Explore_Value_Kicker")}</p>
          <h2 className="explore-display-title">{t("Explore_Value_Title")}</h2>
          <p className="explore-lead">{t("Explore_Value_Lead")}</p>
        </ExploreReveal>

        <div className="explore-statement-grid">
          {STATEMENTS.map((item, index) => {
            const isOpen = openId === item.id;

            return (
              <ExploreReveal
                key={item.id}
                className={`explore-statement explore-statement--${item.tone}${
                  isOpen ? " is-open" : ""
                }`}
                delayMs={index * 80}
              >
                <button
                  type="button"
                  className="explore-statement-trigger"
                  aria-expanded={isOpen}
                  onClick={() =>
                    setOpenId((current) =>
                      current === item.id ? null : item.id,
                    )
                  }
                >
                  <div
                    className="explore-statement-orb"
                    style={{ color: TONE_COLOR[item.tone] }}
                    aria-hidden
                  >
                    {item.icon}
                  </div>
                  <h3 className="explore-statement-headline">
                    {t(item.headlineKey)}
                  </h3>
                  <p
                    className={`explore-statement-detail${
                      isOpen ? " is-open" : ""
                    }`}
                  >
                    {t(item.detailKey)}
                  </p>
                </button>
              </ExploreReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
